import React, { useState } from "react";
import { Box, Typography } from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import { codigosSubcompetencia, etiquetaSubcompetencia, promedioSubcompetencia } from "../../utils/subcompetencias";
import { formatNota } from "../../utils/formato";

const GraficaComparativa = ({ datos, periodoAnterior, periodoReciente, height = 232 }) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  if (!datos || datos.length < 2 || !periodoAnterior || !periodoReciente)
    return null;

  const sorted = [...datos].sort((a, b) => {
    const yearA = Number(a.anio);
    const yearB = Number(b.anio);
    if (yearA !== yearB) return yearA - yearB;
    return Number(a.semestre) - Number(b.semestre);
  });

  const anterior = sorted.find(
    (item) => `${item.anio}-${item.semestre}` === periodoAnterior
  );
  const reciente = sorted.find(
    (item) => `${item.anio}-${item.semestre}` === periodoReciente
  );

  if (!anterior || !reciente) return null;

  // El número de subcompetencias varía por competencia (RC/LC: 3, CC: 7):
  // se descubren en vez de asumir C1-C3.
  const competencias = codigosSubcompetencia([anterior, reciente]).map((codigo) => ({
    key: codigo,
    label: etiquetaSubcompetencia(anterior, codigo) || codigo,
    anterior: promedioSubcompetencia(anterior, codigo),
    reciente: promedioSubcompetencia(reciente, codigo),
  }));

  if (!competencias.length) {
    // Competencias sin subcompetencias (hoy solo CE): en vez de dejar la
    // tarjeta vacía/estirada por el grid, se avisa con el mismo alto normal
    // que tendría la gráfica.
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ height, gap: 8, padding: "0 24px", textAlign: "center" }}
      >
        <WarningRoundedIcon style={{ fontSize: 28, color: "#9e9e9e" }} />
        <Typography variant="body2" style={{ color: "#757575", fontWeight: 600 }}>
          Esta competencia no tiene subcompetencias disponibles.
        </Typography>
      </Box>
    );
  }

  const width = 640;
  const chartHeightPx = height;
  const paddingX = 34;
  const paddingY = 24;
  const chartWidth = width - paddingX * 2;
  const chartHeight = chartHeightPx - paddingY * 2;

  const xStep =
    competencias.length > 1 ? chartWidth / (competencias.length - 1) : 0;

  const toY = (value) =>
    chartHeightPx - paddingY - (value / 5) * chartHeight;

  const handleMouse = (e, comp) => {
    const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();

    const diff = comp.reciente - comp.anterior;
    const percent =
      comp.anterior !== 0 ? (diff / comp.anterior) * 100 : 0;

    setTooltip({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      data: { ...comp, diff, percent },
    });
  };

  return (
    <Box borderRadius={10} style={{ padding: "6px 0 4px" }}>
      <div style={{ width: "100%", height: `${chartHeightPx}px`, position: "relative" }}>
        <svg viewBox={`0 0 ${width} ${chartHeightPx}`} width="100%" height="100%" preserveAspectRatio="none">
          <g>
            {[0, 1, 2, 3, 4, 5].map((tick) => {
              const y = toY(tick);
              return (
                <g key={tick}>
                  <line
                    x1={paddingX}
                    y1={y}
                    x2={width - paddingX}
                    y2={y}
                    stroke="#e2e8f0"
                  />
                  <text
                    x={paddingX - 8}
                    y={y + 4}
                    textAnchor="end"
                    fill="#475569"
                    fontSize="10"
                    fontWeight="600"
                  >
                    {formatNota(tick)}
                  </text>
                </g>
              );
            })}

          {competencias.map((comp, index) => {
            const x = paddingX + xStep * index;
            const yAnterior = toY(comp.anterior);
            const yReciente = toY(comp.reciente);

            return (
              <g key={comp.key}>
                <line
                  x1={x}
                  y1={yAnterior}
                  x2={x}
                  y2={yReciente}
                  stroke="#1976d2"
                  strokeWidth="6"
                  strokeLinecap="round"
                />

                <circle
                  cx={x}
                  cy={yAnterior}
                  r="7"
                  fill="#0f172a"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  onMouseEnter={(e) => handleMouse(e, comp)}
                  onMouseMove={(e) => handleMouse(e, comp)}
                  onMouseLeave={() => setTooltip({ visible: false })}
                />

                <circle
                  cx={x}
                  cy={yReciente}
                  r="7"
                  fill="#1976d2"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  onMouseEnter={(e) => handleMouse(e, comp)}
                  onMouseMove={(e) => handleMouse(e, comp)}
                  onMouseLeave={() => setTooltip({ visible: false })}
                />

                <text
                  x={x}
                  y={chartHeightPx - paddingY + 18}
                  textAnchor="middle"
                  fill="#334155"
                  fontSize="12"
                  fontWeight="600"
                >
                  {comp.key}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {tooltip.visible && tooltip.data && (
        <div
          style={{
            position: "absolute",
            top: tooltip.y - 85,
            left: tooltip.x + 12,
            background: "#0f172a",
            color: "#fff",
            padding: "12px",
            borderRadius: "10px",
            fontSize: "12px",
            zIndex: 10,
            minWidth: "160px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
            transform: "scale(1)",
            opacity: 1,
            transition: "all 0.15s ease",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "14px",
              width: "10px",
              height: "10px",
              background: "#0f172a",
              transform: "rotate(45deg)",
            }}
          />

          <div
            style={{
              fontWeight: "700",
              marginBottom: "8px",
              fontSize: "13px",
            }}
          >
            {tooltip.data.label}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#cbd5e1", fontSize: "11px" }}>
                ● Anterior
              </span>
              <span style={{ fontWeight: "600" }}>
                {formatNota(tooltip.data.anterior)}
              </span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#90caf9", fontSize: "11px" }}>
                ● Reciente
              </span>
              <span style={{ fontWeight: "600" }}>
                {formatNota(tooltip.data.reciente)}
              </span>
            </div>
          </div>

          <div
            style={{
              height: "1px",
              background: "#1e293b",
              margin: "8px 0",
            }}
          />

          <div
            style={{
              fontWeight: "700",
              textAlign: "right",
              color:
                tooltip.data.diff > 0
                  ? "#22c55e"
                  : tooltip.data.diff < 0
                  ? "#ef4444"
                  : "#eab308",
            }}
          >
            {tooltip.data.diff > 0 ? "↑" : tooltip.data.diff < 0 ? "↓" : "→"}{" "}
            {formatNota(tooltip.data.diff)} (
            {tooltip.data.percent.toFixed(1)}%)
          </div>
        </div>
      )}
      </div>
    </Box>
  );
};

export default GraficaComparativa;